import { ChevronLeftIcon, ChevronRightIcon } from '@village/icons';
import { BookingDate } from 'components/fields';
import { addDays, isToday } from 'date-fns';
import { useAppointmentsRange, useBooking, useCaptureCountlyEvent, useIsMobile } from 'hooks';
import { range } from 'ramda';
import { FC, useCallback, useMemo } from 'react';
import { formatDate } from 'utils/date';

import * as Styled from './styles';

interface Props {
    isAppointmentsFetching: boolean;
}

const DatesNavigation: FC<Props> = ({ isAppointmentsFetching }) => {
    const { booking, setBookingField } = useBooking();
    const { datesPadding, datesRange } = useAppointmentsRange();
    const isMobile = useIsMobile();
    const { addCountlyEvent } = useCaptureCountlyEvent();

    const dates = useMemo(() => {
        return range(-(datesPadding ?? 0), datesRange + (datesPadding ?? 0)).map((counter) => {
            const item = addDays(booking.date, counter);

            return {
                isMargin: counter < 0 || counter >= datesRange,
                timestamp: item.getTime(),
                day: formatDate(item, 'dayOnly'),
                monthAndDate: formatDate(item, 'shortMonthDay'),
            };
        });
    }, [booking.date, datesRange, datesPadding]);

    const goToNextPeriod = useCallback(() => {
        setBookingField({ date: addDays(booking.date, datesRange) });
        addCountlyEvent({ key: 'changeDate', segmentation: { changeDateType: 'add' } });
    }, [booking.date, datesRange, setBookingField, addCountlyEvent]);

    const goToPreviousPeriod = useCallback(() => {
        setBookingField({ date: addDays(booking.date, -datesRange) });
        addCountlyEvent({ key: 'changeDate', segmentation: { changeDateType: 'subtract' } });
    }, [booking.date, datesRange, setBookingField, addCountlyEvent]);

    return (
        <Styled.Container>
            <Styled.NavigationButton
                disabled={isToday(booking.date) || isAppointmentsFetching}
                onClick={goToPreviousPeriod}
                size="small"
                variant="secondary"
            >
                <ChevronLeftIcon size="sm" />
            </Styled.NavigationButton>
            <Styled.DatesContainer>
                {isMobile ? (
                    <BookingDate />
                ) : (
                    dates.map((item) => (
                        <Styled.DateContainer key={item.timestamp} $highlight={!item.isMargin}>
                            <Styled.Day>{item.day}</Styled.Day>
                            <Styled.DayAndMonth>{item.monthAndDate}</Styled.DayAndMonth>
                        </Styled.DateContainer>
                    ))
                )}
            </Styled.DatesContainer>
            <Styled.NavigationButton disabled={isAppointmentsFetching} onClick={goToNextPeriod} size="small" variant="secondary">
                <ChevronRightIcon size="sm" />
            </Styled.NavigationButton>
        </Styled.Container>
    );
};

export { DatesNavigation };
